import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { replace } from 'lib/routing/appRouting'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import usePrevious from 'hooks/usePrevious'
import { useGlobalExperiencesSearchContext, useGlobalExperiencesSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import useExperiencesSearchFilters from '../../Experiences/hooks/useExperiencesSearchFilters'
import useExperiencesSortOptions from '../../Experiences/hooks/useExperiencesSortOptions'
import { usePlaceAncestorsById, usePlaceById } from 'hooks/usePlace'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import useExperiences from 'hooks/Experiences/useExperiences'
import useExperiencesBatched from 'hooks/Experiences/useExperiencesBatched'
import GeoContext from 'contexts/geoContext'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import Places, { getPlaceByRegionCode } from 'constants/places'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { EmptyArray, without, fillArray } from 'lib/array/arrayUtils'
import { isCoordinateWithinBounds, isValidMapBounds, normaliseLongitude, parseMapBounds, serialiseMapBounds } from 'lib/maps/bounds'
import type { MapBounds } from 'lib/maps/types'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Error404Page from 'components/Pages/Error404Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LayoutContainer from 'components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import OfferListPagination from 'components/OfferList/OfferListPagination'
import ExperienceSearchMap from './ExperienceSearchMap'
import ExperienceSearchTile from '../ExperienceTiles/ExperienceSearchTile'
import { listHasPlaceId } from './utils'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ExperienceSearchHeader from './ExperienceSearchHeader'
import ExperienceSearchPageFiltersSidebar from './ExperienceSearchPageFiltersSidebar'
import useToggle from 'hooks/useToggle'
import Pane from 'components/Common/Pane'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import HeadData from 'components/Common/HeadData'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import { getExperiencesSearchHeadMeta } from './getExperiencesSearchHeadMeta'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE } from 'contexts/Experience/GlobalSearchState'
import useAddExperienceRecentSearch from 'components/Experiences/hooks/useAddExperienceRecentSearch'
import useExperiment from 'hooks/Experiments/useExperiment'
import { ChertoExperiments } from 'constants/experiments'
import RedirectWithStatus from 'components/Common/RedirectWithStatus/RedirectWithStatus'

const MAP_MARKER_EXPERIENCE_LIMIT = 700

const Root = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const MapContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const ToggleMapScreenButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  grid-area: map;
  margin: ${rem(16)};
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
`

const FilterButton = styled(AssistChip)`
  position: absolute;
  top: ${rem(16)};
  inset-inline-start: ${rem(16)};
  z-index: 10;
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
  display: none;

  @media (max-width: 1023px) {
    display: inline-flex;
  }
`

const OfferListContainer = styled.div`
  flex: 0 1 50%;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: scroll;
  height: 100%;
  transition: flex-basis .3s ease-in-out, opacity .3s ease-in-out, visibility .3s ease-in-out;

  @media (min-width: 2048px) {
    flex-basis: 40%;
  }
`

const OfferListContent = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-inline: ${rem(24)} ${rem(16)};
  gap: ${rem(16)};
  margin-top: ${rem(16)};
  margin-bottom: ${rem(16)};
`

const MapViewContainer = styled.div`
  flex: 0 1 50%;
  min-width: 0;
  height: 100%;
  transition: flex-basis .3s ease-in-out;

  @media (min-width: 2048px) {
    flex-basis: 60%;
  }
`

const MainContainer = styled(CSSBreakpoint)`
  flex: 1;
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 0;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.surface.lightForeground};

  &.full-screen-map {
    ${OfferListContainer} {
      display: none;
      flex-basis: 0;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }

    ${MapViewContainer} {
      flex: 1 1 100%;
    }
  }
`

const SkeletonTile = styled(LoadingBox)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  min-height: ${rem(284)};
`

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const Box = styled.div`
  flex: 1;
`

const StyledGroup = styled(Group)`
  padding: ${rem(16)} 0;
`

interface MapPageProps {
  search: string
}

interface Props extends MapPageProps {}

function ExperiencesSearchMapPageContent({ search }: Props) {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)
  const [page, setPage] = useState<number>(0)
  const [fullScreenMap, setFullScreenMap] = useState(false)
  const [overrideFetching, setOverrideFetching] = useState<boolean>(false)
  const offerListContainerRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const tilesRootRef = useRef<HTMLDivElement>(null)
  const pageSize = 20

  const placeId = queryParams.get('destinationId') ?? undefined
  const {
    searchFilters,
  } = useExperiencesSearchFilters()

  const { globalExperiencesSearchDispatch, globalExperiencesSearchState } = useGlobalExperiencesSearchContext({
    ...GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE,
    experienceCategories: searchFilters.categoryCodes ?? [],
    experienceCollections: searchFilters.collections ?? [],
  }, queryParams)

  const { sortBy } = useExperiencesSortOptions()

  const [showMobileFilter, toggleShowMobileFilter] = useToggle()

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  useAddExperienceRecentSearch(queryParams)

  const regionPlace = useMemo(() => getPlaceByRegionCode(geo.currentRegionCode), [geo.currentRegionCode])

  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(placeId)

  const fallbackPlaceIds = useMemo(() => {
    if (searchFilters.collections?.length) {
      return EmptyArray
    }

    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...new Set([...ancestorIds, regionPlace.id])]
  }, [ancestorPlaceIds, regionPlace, searchFilters.collections])

  const list = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? [] : fallbackPlaceIds,
    categoryCodes: searchFilters.categoryCodes,
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    campaigns: searchFilters.campaigns,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    leExclusive: searchFilters.leExclusive,
    sortBy,
    bounds: searchFilters.bounds,
    collections: searchFilters.collections ?? [],
    from: searchFilters.checkIn,
    to: searchFilters.checkOut,
  })

  // List without category filters (for category counts)
  const listForCounts = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? [] : fallbackPlaceIds,
    categoryCodes: [], // No category filters for counts
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    campaigns: searchFilters.campaigns,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    leExclusive: searchFilters.leExclusive,
    sortBy,
    bounds: searchFilters.bounds,
    collections: searchFilters.collections ?? [],
    from: searchFilters.checkIn,
    to: searchFilters.checkOut,
  })

  // Use filtered list when categories are selected, otherwise use the full list for counts
  const totalItems = (searchFilters.categoryCodes && searchFilters.categoryCodes.length > 0)
    ? list.ids.length
    : listForCounts.ids.length

  const experienceIdsLimited = useMemo(() => {
    const startIndex = page * pageSize
    const endIndex = startIndex + pageSize
    return list.ids.slice(startIndex, endIndex)
  }, [list.ids, page, pageSize])

  const [experiences, fetchingExperiences] = useExperiences(experienceIdsLimited)

  const isAnywhereSearch = list.placeId === Places.Anywhere.id

  const mapExperienceIds = useMemo(() => {
    const markerExperienceIds = list.ids.slice(0, MAP_MARKER_EXPERIENCE_LIMIT)

    return [...new Set([
      ...experienceIdsLimited,
      ...markerExperienceIds,
    ])]
  }, [experienceIdsLimited, list.ids])

  const [mapExperiences, fetchingMapExperiences] = useExperiencesBatched(mapExperienceIds)

  const mapSearchBounds = useMemo(() => {
    if (!searchFilters.bounds) {
      return undefined
    }

    const parsedBounds = parseMapBounds(searchFilters.bounds)
    return isValidMapBounds(parsedBounds) ? parsedBounds : undefined
  }, [searchFilters.bounds])

  const mapExperiencesById = useMemo(() => {
    return new Map(mapExperiences.map(experience => [experience.id, experience]))
  }, [mapExperiences])

  const visibleMapExperienceIds = useMemo(() => {
    if (!mapSearchBounds) {
      return []
    }

    return list.ids.filter((id) => {
      const experience = mapExperiencesById.get(id)
      const location = experience?.location

      if (!location) {
        return false
      }

      return isCoordinateWithinBounds({
        lat: location.latitude,
        lng: location.longitude,
      }, mapSearchBounds)
    })
  }, [list.ids, mapExperiencesById, mapSearchBounds])

  const visibleMapExperienceIdsLimited = useMemo(() => {
    if (!mapSearchBounds) {
      return []
    }

    const startIndex = page * pageSize
    const endIndex = startIndex + pageSize
    return visibleMapExperienceIds.slice(startIndex, endIndex)
  }, [mapSearchBounds, page, pageSize, visibleMapExperienceIds])

  const visibleMapExperiences = useMemo(() => {
    const nextExperiences: Array<App.ExperienceOffer> = []

    for (const id of visibleMapExperienceIdsLimited) {
      const experience = mapExperiencesById.get(id)

      if (experience) {
        nextExperiences.push(experience)
      }
    }

    return nextExperiences
  }, [mapExperiencesById, visibleMapExperienceIdsLimited])

  const listExperiences = mapSearchBounds ? visibleMapExperiences : experiences
  const listTotalItems = mapSearchBounds ? visibleMapExperienceIds.length : totalItems

  const place = usePlaceById(list.placeId)
  const destinationBounds = useMemo(() => {
    if (isAnywhereSearch) {
      return undefined
    }

    const boundingRadius = place?.boundingRadius

    if (!boundingRadius) {
      return undefined
    }

    const { latitude, longitude } = boundingRadius.centre
    const latDelta = boundingRadius.radius / 111320
    const lngDelta = boundingRadius.radius / (111320 * Math.cos(latitude * Math.PI / 180))

    const nextBounds = {
      west: normaliseLongitude(longitude - lngDelta),
      south: latitude - latDelta,
      east: normaliseLongitude(longitude + lngDelta),
      north: latitude + latDelta,
    }

    return isValidMapBounds(nextBounds) ? nextBounds : undefined
  }, [isAnywhereSearch, place?.boundingRadius])
  const metaPlaceName = !isAnywhereSearch ? place?.name : undefined

  const searchHeadMeta = useMemo(() => getExperiencesSearchHeadMeta({
    placeName: metaPlaceName,
    isAnywhere: isAnywhereSearch,
    intl,
  }), [metaPlaceName, isAnywhereSearch, intl])

  const previousListIds = usePrevious(list.ids)

  useEffect(() => {
    if (previousListIds && previousListIds !== list.ids) {
      setPage(0)
      offerListContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [list.ids, previousListIds])

  const isFetching = list.fetching || fetchingExperiences || overrideFetching
  const isMapFetching = isFetching || fetchingMapExperiences
  const isListFetching = mapSearchBounds ? isMapFetching : isFetching

  useGlobalExperiencesSearchResultsTracking(globalExperiencesSearchState, list.ids, !!list.fetching)

  const searchMode = useMemo(() => {
    const searchQuery = new URLSearchParams(search)
    searchQuery.delete('bounds')
    searchQuery.delete('pages')
    const queryString = searchQuery.toString()

    return {
      url: `/search/experiences${queryString ? `?${queryString}` : ''}`,
      mode: 'map',
    }
  }, [search])

  const resetLoadingState = useMemo(
    () => debounce(() => setOverrideFetching(false), 750),
    [],
  )

  useEffect(() => {
    return () => resetLoadingState.cancel()
  }, [resetLoadingState])

  const handleOfferListLoading = useCallback(() => {
    setOverrideFetching(true)
    resetLoadingState()
  }, [resetLoadingState])

  const handleMapScreenToggle = useCallback(() => {
    setFullScreenMap(!fullScreenMap)
  }, [fullScreenMap])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
    dispatch(replace({ search: setSearchParamValue(search, 'pages', Math.max(newPage, 1)) }))
  }, [dispatch, search])

  const onBoundsChanged = useCallback((newBounds: MapBounds) => {
    if (!newBounds) { return }
    handleOfferListLoading()
    setPage(0)
    const bounds = serialiseMapBounds(newBounds)
    const newSearch = setSearchParamValue(search, 'bounds', bounds)
    dispatch(replace({ search: newSearch }))
  }, [handleOfferListLoading, search, dispatch])

  if (!listHasPlaceId(list)) {
    return <Error404Page />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
    <GlobalSearchContextProvider
      state={globalExperiencesSearchState}
      dispatch={globalExperiencesSearchDispatch}
    >
      <HeadData
        noIndex
        title={searchHeadMeta.title}
        description={searchHeadMeta.description}
        imageId={searchHeadMeta.imageId}
        imageUrl={searchHeadMeta.imageUrl}
        imageAlt={searchHeadMeta.imageAlt}
      />
      <Root ref={rootRef}>
        <Pane shadow="bottom-small">
          <ExperienceSearchPageFiltersSidebar
            open={showMobileFilter}
            place={place}
            onClose={toggleShowMobileFilter}
          />
          <ExperienceSearchHeader
            list={list}
            searchMode={searchMode}
            place={place}
          />
        </Pane>
        <MapContent ref={tilesRootRef} data-experience-search-tiles-root>
          <MapWrapper>
            <MainContainer min="largeDesktop" className={clsx({ 'full-screen-map': fullScreenMap })}>
              <OfferListContainer ref={offerListContainerRef}>
                <OfferListContent>
                  <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                    <div>
                      {!isListFetching && <BodyText variant="large">
                        {intl.formatMessage({
                          id: 'r6RhxYF6oFPd98rfe689',
                          defaultMessage: '{count, plural, one {# experience} other {# experiences}} found',
                          description: 'Count of experiences found in map view search results',
                        }, { count: listTotalItems })}
                      </BodyText>}
                      {isListFetching && <Loader inline />}
                    </div>
                    <ExperienceSearchPageSort />
                  </Group>
                  {(!listExperiences || listExperiences.length === 0 && isListFetching) && fillArray(5).map((id) => <SkeletonTile key={id} />)}
                  {!!listExperiences.length && listExperiences.map((experience, index) => (
                    <ExperienceSearchTile
                      key={experience.id}
                      position={index}
                      experience={experience}
                      isMapView
                    />
                  ))}
                  {!isListFetching && !listExperiences.length && (
                    <StyledGroup direction="vertical" horizontalAlign="center" verticalAlign="center" gap={8}>
                      <Heading variant="heading3" align="center">
                        {intl.formatMessage({
                          id: 'idb5aeSuLpdq5lqVzVK0',
                          defaultMessage: 'Sorry, we couldn’t find any experiences that match your search criteria.',
                          description: 'Heading shown when no experiences match filters on map view',
                        })}
                      </Heading>
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'rLWW03NhtS6ywI18oqeZ',
                          defaultMessage: 'Try removing some filters or searching a different location to see more options.',
                          description: 'Suggestion text when no experiences match search criteria on map view',
                        })}
                      </BodyText>
                    </StyledGroup>
                  )}
                  <OfferListPagination
                    offerListRef={offerListContainerRef}
                    page={page}
                    total={listTotalItems}
                    itemsPerPage={pageSize}
                    handlePageChange={handlePageChange}
                  />
                </OfferListContent>
              </OfferListContainer>
              <MapViewContainer>
                <ToggleMapScreenButton
                  kind="primary"
                  variant="ghost"
                  size="large"
                  shape="square"
                  onClick={handleMapScreenToggle}
                >
                  {fullScreenMap ? (
                    <LineAngleRightIcon />
                  ) : (
                    <LineAngleLeftIcon />
                  )}
                </ToggleMapScreenButton>
                <ExperienceSearchMap
                  experiences={mapExperiences}
                  totalItems={totalItems}
                  destinationBounds={destinationBounds}
                  fetching={isMapFetching}
                  onBoundsChanged={onBoundsChanged}
                  rootRef={tilesRootRef}
                />
              </MapViewContainer>
            </MainContainer>
            <MainContainer max="desktop">
              <Box>
                <FilterButton
                  size="medium"
                  variant="filled"
                  startIcon={<LineFilterAltIcon />}
                  onClick={toggleShowMobileFilter}
                  data-testid="mobile-filter-button"
                >
                  {intl.formatMessage({
                    id: 'NoO7dLfI_Q5bneVbfLgE',
                    defaultMessage: 'Filter',
                    description: 'Mobile filter button text on experience search map page',
                  })}
                </FilterButton>
                <ExperienceSearchMap
                  experiences={mapExperiences}
                  totalItems={totalItems}
                  destinationBounds={destinationBounds}
                  fetching={isMapFetching}
                  onBoundsChanged={onBoundsChanged}
                  rootRef={tilesRootRef}
                  hideOfferCarousel={showMobileFilter}
                />
              </Box>
            </MainContainer>
          </MapWrapper>
        </MapContent>
      </Root>
    </GlobalSearchContextProvider>
  </AnalyticsPageContext.Provider>
}

function ExperiencesSearchMapPage(props: Props) {
  const queryParams = useQueryParams()
  const { isAnyVariant: contentTagsEnabled } = useExperiment(ChertoExperiments.experiencesContentTagSearch)

  if (contentTagsEnabled && queryParams.has('contentTagId')) {
    return <RedirectWithStatus code={302} to={`/search/experiences${props.search}`} />
  }

  return <ExperiencesSearchMapPageContent {...props} />
}

const mapStateToProps = (state: App.State) => ({
  search: state.router.location.search,
})

export default connect(mapStateToProps)(ExperiencesSearchMapPage)
